import {Injectable} from '@angular/core';
import {UserService} from '../user/user.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {ModalService} from '../modal/modal.service';
import {
  FeContactCommercialModalComponent
} from '../../components/modals/fe-contact-commercial-modal/fe-contact-commercial-modal.component';
import {
  FeAnnouncementModalComponent
} from '../../components/modals/fe-announcement-modal/fe-announcement-modal.component';
import {FEATURE_PRESENTATIONS, PRODUCT_ANNOUNCEMENTS, ProductAnnouncement} from '../../config/feature-presentations';

/**
 * Fonctionnalités non incluses dans l'offre du client : snackbar "Fonctionnalité indisponible"
 * avec un bouton "Contacter mon conseiller" ouvrant la modale de demande de contact,
 * et annonces de nouveautés (encart + modale de présentation).
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureAccessService {

  // Fonctionnalités dont la tentative d'accès est tracée (v1/clients/accessFeature)
  private static readonly TRACKED_FEATURES: string[] = ['dashboard', 'datAnalyz', 'decisions_admin'];
  // Suffixe indiquant au backend un clic sur l'encart d'une annonce (et non sur la fonctionnalité elle-même)
  private static readonly ANNOUNCEMENT_SUFFIX: string = '_encart';
  private static readonly DISMISSED_KEY_PREFIX: string = 'fe_announcement_dismissed';

  constructor(private userService: UserService,
              private snackBarService: SnackbarService,
              private modalService: ModalService) {
  }

  /**
   * Option "données institutionnelles" du client (décisions administratives, origine de la source, données chiffrées)
   */
  public hasInstitutionalData(): boolean {
    return !!this.userService.sessionData?.customerDetails?.hasInstitutionalData;
  }

  public showFunctionDisabled(name: string): void {
    if (FeatureAccessService.TRACKED_FEATURES.includes(name)) {
      this.userService.accessFeature(name).subscribe();
    }
    const presentation = FEATURE_PRESENTATIONS[name];
    const text = presentation ? presentation.text : 'Cette fonctionnalité n\'est pas disponible avec votre offre actuelle.';
    const titleAskContactModal = presentation ? presentation.askContactTitle : '';
    this.snackBarService.showDangerSnackbar('Fonctionnalité indisponible', text, 15, args => {
      this.modalService.showCustomModal(FeContactCommercialModalComponent, {
        data: {
          salesPersonDetails: this.userService.sessionData.salesPersonDetails,
          feature: name,
          title: titleAskContactModal
        }
      });
    }, 'Contacter mon conseiller');
  }

  /**
   * Première annonce à afficher pour le client : éligible selon sa session et non masquée.
   */
  public getActiveAnnouncement(): ProductAnnouncement | undefined {
    const session = this.userService.sessionData;
    if (!session || !session.customerDetails || session.isScope) {
      return undefined;
    }
    return PRODUCT_ANNOUNCEMENTS.find(announcement =>
        announcement.isEligible(session) && !this.isAnnouncementDismissed(announcement.id));
  }

  public openAnnouncement(announcement: ProductAnnouncement): void {
    this.userService.accessFeature(announcement.feature + FeatureAccessService.ANNOUNCEMENT_SUFFIX).subscribe();
    this.modalService.showCustomModal(FeAnnouncementModalComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        announcement: announcement,
        text: FEATURE_PRESENTATIONS[announcement.feature].text,
        salesPersonDetails: this.userService.sessionData.salesPersonDetails
      }
    });
  }

  public dismissAnnouncement(announcementId: string): void {
    try {
      localStorage.setItem(this.getDismissedKey(announcementId), 'true');
    } catch (e) {
      // stockage indisponible : l'annonce réapparaîtra au prochain chargement
    }
  }

  private isAnnouncementDismissed(announcementId: string): boolean {
    try {
      return localStorage.getItem(this.getDismissedKey(announcementId)) === 'true';
    } catch (e) {
      return false;
    }
  }

  // Clé par utilisateur : un même navigateur peut servir plusieurs comptes
  private getDismissedKey(announcementId: string): string {
    return FeatureAccessService.DISMISSED_KEY_PREFIX + ':' + this.userService.sessionData?.customerDetails?.user_id
        + ':' + announcementId;
  }
}
